import React, { useState } from 'react';
import { Box, Button } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import AuthorizedRoute from './AuthorizedRoute';
import SinUbicacionesAsignadasAlert from './shared/SinUbicacionesAsignadasAlert';
import SinSucursalesAsignadasAlert from './shared/SinSucursalesAsignadasAlert';
import CertificadoArcaVencimientoAlert from './shared/CertificadoArcaVencimientoAlert';
import { UbicacionesUsuarioProvider, useUbicacionesUsuarioContext } from '../contexts/UbicacionesUsuarioContext';
import { SucursalesUsuarioProvider, useSucursalesUsuarioContext } from '../contexts/SucursalesUsuarioContext';
import { FechaOperativaProvider } from '../contexts/FechaOperativaContext';
import { PageTourProvider } from './tour/PageTourContext';
import { allModuleMenus } from '../config/navigationMenu';
import { administrationMenuItem, getAdministracionBackPath, sistemaModulePath } from '../config/adminNavigation';
import {
  readAdminBackTo,
  isValidAdminBackTarget,
  type AdminNavigationState,
} from '../utils/adminNavigationContext';
import {
  resolveModuleBackForPath,
  type ModuleNavigationState,
} from '../utils/moduleNavigationContext';
import PageBreadcrumbs from './shared/PageBreadcrumbs';
import { resolveBreadcrumbsFromPath } from '../utils/pageBreadcrumbs';

const LayoutContent: React.FC = () => {
  const { sinAsignaciones, esAdmin } = useUbicacionesUsuarioContext();
  const { sinAsignaciones: sinSucursalesAsignadas, esAdmin: esAdminSucursales } =
    useSucursalesUsuarioContext();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [pageHelpOpen, setPageHelpOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  // Leer el estado guardado del sidebar o usar true por defecto
  const [desktopOpen, setDesktopOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    return saved !== null ? saved === 'true' : true;
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopToggle = () => {
    const newState = !desktopOpen;
    setDesktopOpen(newState);
    // Guardar el estado en localStorage
    localStorage.setItem('sidebarOpen', String(newState));
    window.dispatchEvent(new CustomEvent('sidebar-open-change', { detail: { open: newState } }));
  };

  // Incluye módulos condicionales (CRM / Servicio / Producción / Fashion) para "Volver al Menu".
  // Paths más largos primero para que /inventario/fashion gane sobre /inventario.
  const modulePaths = [
    administrationMenuItem.path,
    sistemaModulePath,
    ...allModuleMenus()
      .filter((item) => item.subItems && item.subItems.length > 0)
      .map((item) => item.path),
  ].sort((a, b) => b.length - a.length);

  const moduloActualPath = modulePaths.find(
    (modulePath) =>
      location.pathname === modulePath ||
      location.pathname.startsWith(`${modulePath}/`)
  );

  /** Listas de precios viven bajo /ventas pero el acceso habitual es desde Inventario → Mantenimiento precios. */
  const esListasPreciosVentas =
    /^\/ventas\/listaprecios(\/|$)/.test(location.pathname) ||
    /^\/ventas\/lista-precios(\/|$)/.test(location.pathname);

  const destinoAdministracion = getAdministracionBackPath(
    location.pathname,
    [((location.state as AdminNavigationState | null)?.adminBackTo), readAdminBackTo()].find(
      (path) => path && isValidAdminBackTarget(path),
    ) ?? null,
  );

  const moduleBackForSharedRoute = resolveModuleBackForPath(
    location.pathname,
    (location.state as ModuleNavigationState | null)?.moduleBackTo,
  );

  const destinoVolverMenuRaw = esListasPreciosVentas
    ? '/inventario'
    : destinoAdministracion ?? moduleBackForSharedRoute ?? moduloActualPath;

  const destinoVolverMenu =
    destinoVolverMenuRaw && destinoVolverMenuRaw !== location.pathname
      ? destinoVolverMenuRaw
      : moduloActualPath && moduloActualPath !== location.pathname
        ? moduloActualPath
        : destinoVolverMenuRaw;

  const mostrarBarraVolverModulo = Boolean(
    moduloActualPath && location.pathname !== moduloActualPath
  );

  const breadcrumbItems = resolveBreadcrumbsFromPath(location.pathname);
  const mostrarBreadcrumbs = breadcrumbItems.length >= 2;

  return (
    <Box className="app-shell" sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      <Box className="app-content-row" sx={{ display: 'flex', flexGrow: 1 }}>
        <Header
          onMenuClick={handleDrawerToggle}
          desktopOpen={desktopOpen}
          onDesktopToggle={handleDesktopToggle}
        />
        <Sidebar 
          mobileOpen={mobileOpen} 
          onMobileClose={handleDrawerToggle}
          desktopOpen={desktopOpen}
          onDesktopToggle={handleDesktopToggle}
        />
        <Box
          className="app-main"
          component="main"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            pl: { xs: 2, sm: 3 },
            pr: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 },
            pt: mostrarBarraVolverModulo || mostrarBreadcrumbs ? 0 : { xs: 2, sm: 3 },
            mt: 8, // Para dejar espacio para el header
            backgroundColor: '#f5f5f5',
            overflowX: 'hidden',
            overflowY: 'auto',
            minWidth: 0,
            width: { xs: '100%', md: desktopOpen ? `calc(100% - 280px)` : `calc(100% - 72px)` },
            transition: (t) =>
              t.transitions.create('width', {
                easing: t.transitions.easing.sharp,
                duration: t.transitions.duration.enteringScreen,
              }),
          }}
        >
          <Box className="app-outlet-wrap" sx={{ flexGrow: 1, minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>
            {mostrarBreadcrumbs && (
              <Box
                className="app-breadcrumbs"
                sx={{
                  pt: { xs: 1.5, sm: 2 },
                  pb: { xs: 0.5, sm: 0.75 },
                  pl: { xs: 2, sm: 3 },
                  pr: { xs: 2, sm: 3 },
                }}
              >
                <PageBreadcrumbs items={breadcrumbItems} />
              </Box>
            )}
            {mostrarBarraVolverModulo && moduloActualPath && destinoVolverMenu && (
              <Box
                className="app-back-to-module"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pt: mostrarBreadcrumbs ? 0 : { xs: 1.5, sm: 2 },
                  pb: { xs: 0.5, sm: 1 },
                  pl: { xs: 2, sm: 3 },
                  pr: { xs: 2, sm: 3 },
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<ArrowBack />}
                  onClick={() => navigate(destinoVolverMenu)}
                  sx={{ textTransform: 'none' }}
                >
                  Volver al Menu
                </Button>
              </Box>
            )}
            {sinAsignaciones && <SinUbicacionesAsignadasAlert esAdmin={esAdmin} />}
            {sinSucursalesAsignadas && <SinSucursalesAsignadasAlert esAdmin={esAdminSucursales} />}
            <CertificadoArcaVencimientoAlert />
            {/* Permisos solo sobre el contenido: evita desmontar Header/Sidebar (parpadeo). */}
            <AuthorizedRoute>
              <Outlet />
            </AuthorizedRoute>
          </Box>
          <Footer />
        </Box>
      </Box>
    </Box>
  );
};

const Layout: React.FC = () => (
  <UbicacionesUsuarioProvider>
    <SucursalesUsuarioProvider>
      <FechaOperativaProvider>
        <PageTourProvider>
          <LayoutContent />
        </PageTourProvider>
      </FechaOperativaProvider>
    </SucursalesUsuarioProvider>
  </UbicacionesUsuarioProvider>
);

export default Layout; 